/**
 * useVariantedSvg — derives a variant-aware SVG element from a base plan.
 *
 * If `selectedVariants` is all-zeros (or empty), returns the base element
 * unchanged — no clone, no DOM work. Otherwise returns a fresh clone with
 * `applyCompartmentVariants` already applied.
 *
 * The returned reference changes whenever `selectedVariants` or
 * `getVariantNode` change, which triggers a re-mount in mode renderers that
 * key their setup effects off `svgElement` (Info, Insolation, HotSpot).
 *
 * Arrange mode uses the **base** SVG directly so it can keep its in-place
 * diff-based mutation strategy (zoom/pan/scroll stability while cycling).
 *
 * @param {object} params
 * @param {SVGElement|null} params.baseSvg
 * @param {number[]} params.selectedVariants
 * @param {(compartmentNumber:number, version:number) => SVGElement|null} params.getVariantNode
 * @returns {SVGElement|null}
 */

import { useMemo } from 'react'

import { applyCompartmentVariants } from '../utils/svgArrange.js'

export function useVariantedSvg({ baseSvg, selectedVariants, getVariantNode }) {
  return useMemo(() => {
    if (!baseSvg) return null
    if (!Array.isArray(selectedVariants) || selectedVariants.length === 0) return baseSvg
    if (selectedVariants.every((v) => v === 0)) return baseSvg

    const clone = baseSvg.cloneNode(true)
    applyCompartmentVariants({
      targetSvg: clone,
      baseSvgElement: baseSvg,
      selectedVariants,
      getVariantNode,
      // No prevVariants — apply everything from scratch on the fresh clone.
    })
    return clone
  }, [baseSvg, selectedVariants, getVariantNode])
}
